/* Copyright (C) nexleader - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written for nexleader <myipsat.com>, 2016-2026
 */

/*global angular*/
import { AuthService } from './../../../../services/auth.service';
import { ErrorHandlerService } from './../../../../services/error-handler.service';
import { EnumsService } from './../../../../services/enum.service';
import { UserService } from './../../../../services/user.service';
import { SurveyResultService } from './../survey-result/survey-result.service';
import {
  AfterViewChecked,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CommonModule, KeyValue } from '@angular/common';
import { NexleaderVimeoVideoComponent } from '../../../small-groups/components/vimeo-video/vimeo-video.component';
import { SurveyResultCalculationsService } from '../survey-result/survey-result-calculations.service';
import { Subscription, catchError, forkJoin, of, tap } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Chart } from 'chart.js';
import { CHART_COLORS } from '../../../../../../constants';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ArchetypeReportService, ArchetypeReport } from '../../../archetype-report/resources/archetype-report.service';
import { PurchaseArchetypeReportModalComponent } from '../../../archetype-report/components/modals/purchase-archetype-report-modal/purchase-archetype-report-modal.component';
import { ARCHETYPE_DISPLAY_NAMES } from '../../../archetype-report/engine/archetype-engine';
import { ArchetypeWheelComponent } from '../../../archetype-report/components/archetype-wheel/archetype-wheel.component';

@Component({
  selector: 'app-nexleader-survey-results',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NexleaderVimeoVideoComponent,
    ArchetypeWheelComponent,
  ],
  templateUrl: './survey-results.component.html',
})
export class NexleaderSurveyResultsComponent
  implements OnInit, AfterViewChecked, OnDestroy {
  routeParams: any;
  ipsatResults: any[] = [];
  user: { [key: string]: any } = {};
  categories: any[] = [];
  possessivePrefix: string | undefined;
  surveyType!: string;
  canvasSubscription!: Subscription;
  @ViewChildren('canvas') canvas!: QueryList<HTMLCanvasElement>;
  private chartInstances: Chart[] = [];
  public doughnutStyle: { [key: string]: string } = {};
  
  // IPSAT 2.0 Archetype Report
  archetypeReports: Map<string, ArchetypeReport> = new Map();
  archetypeDisplayNames = ARCHETYPE_DISPLAY_NAMES;
  canPurchaseArchetypeReport: boolean = false;
  currentUserId: string = '';

  constructor(
    private route: ActivatedRoute,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private authService: AuthService,
    private enumsService: EnumsService,
    private surveyResultService: SurveyResultService,
    private surveyResultCalculationsService: SurveyResultCalculationsService,
    private archetypeReportService: ArchetypeReportService,
    private modalService: BsModalService
  ) { }

  public onCompare(
    _left: KeyValue<any, any>,
    _right: KeyValue<any, any>
  ): number {
    return 1;
  }

  ngOnInit(): void {
    this.routeParams = this.route.snapshot.params;
    const userID = this.routeParams['user_id'];
    // `user` is absent on a consolidated multigroup coach token; fall back to
    // primary_user so purchasedByUserId is attributed to a real account rather
    // than undefined. See the note on canPurchaseArchetypeReport below.
    const bootPayload = this.authService.getTokenPayload();
    this.currentUserId = bootPayload?.user || bootPayload?.primary_user || '';

    // Declare the style to show elements as when printing
    this.doughnutStyle = {
      'max-width': '290px',
      'margin-left': 'auto',
      'margin-right': 'auto',
    };

    forkJoin([
      this.surveyResultService.queryUser(userID),
      this.userService.getUser(userID),
      this.enumsService.getEnums(),
    ])
      .pipe(
        tap(([ipsatResults, user, enums]) => {
          this.ipsatResults = ipsatResults;

          this.user = user;
          this.surveyType = user.group.surveyType.identifier;

          this.categories = enums.AllQuestionCategories;
          
          // Check if current user can purchase archetype reports
          // (owner, coach, group admin, or nexleader admin)
          //
          // Multigroup coaches (Steve 2026-09-18: "Multigroup coaches do not
          // see the 2.0 unlock button") sign in with a completely different
          // token shape. A consolidated coach token carries
          // `is_coach_user: true`, `primary_user` and a `users` array, and has
          // NO `user` field and NO `roles` field at all -- the backend
          // synthesises roles: ['multigroup_coach'] at decode time, which the
          // frontend never sees. So every branch below evaluated false and no
          // multigroup coach could ever see the button. 24 coach accounts on
          // production were affected.
          const tokenPayload = this.authService.getTokenPayload();
          const roles: string[] = tokenPayload?.roles || [];
          const userId = this.user['_id'];

          // The signed-in person is this user, under either token shape.
          const isSelf =
            userId === tokenPayload?.user ||
            userId === tokenPayload?.primary_user ||
            (tokenPayload?.users || []).indexOf(userId) >= 0;

          this.canPurchaseArchetypeReport =
            isSelf ||
            tokenPayload?.is_coach_user === true ||
            roles.includes('coach') ||
            roles.includes('groupadmin') ||
            roles.includes('admin');
        }),
        catchError((error) => {
          this.errorHandlerService.handleError(error);
          return of(null);
        })
      )
      .subscribe(() => {
        this.possessivePrefix =
          this.user['_id'] === this.authService.getTokenPayload().user
            ? 'Your'
            : `${this.user['firstName']}'s`;

        // this.calculations = SurveyResultCalculations;

        this.ipsatResults.forEach((result) => {
          result.total =
            this.surveyResultCalculationsService.calculateTotalIpsatScore(
              result
            );
          result.categories = this.categories;

          result.categories.forEach((category: any) => {
            category.value = 0;
            result.questions.forEach((question: any) => {
              if (question.questionCategory.index === category.index) {
                category.value += question.answer.value;
              }
            });
          });

          result.chartData =
            this.surveyResultCalculationsService.calculateDoughnutChart(
              result.categories,
              result.total
            );
            
          // Load archetype report for this survey result
          this.loadArchetypeReport(result._id);
        });
      });
  }
  
  /**
   * Load archetype report for a survey result if it exists
   */
  loadArchetypeReport(surveyResultId: string): void {
    this.archetypeReportService
      .getBySurveyResult(surveyResultId)
      .pipe(
        tap((report) => {
          if (report) {
            this.archetypeReports.set(surveyResultId, report);
          }
        }),
        catchError(() => of(null))
      )
      .subscribe();
  }
  
  /**
   * Check if a survey result has an archetype report
   */
  hasArchetypeReport(surveyResultId: string): boolean {
    return this.archetypeReports.has(surveyResultId);
  }
  
  /**
   * Get archetype report for a survey result
   */
  getArchetypeReport(surveyResultId: string): ArchetypeReport | undefined {
    return this.archetypeReports.get(surveyResultId);
  }
  
  /**
   * Open purchase modal for archetype report
   */
  openPurchaseModal(surveyResult: any): void {
    const modalRef = this.modalService.show(PurchaseArchetypeReportModalComponent, {
      initialState: {
        surveyResultId: surveyResult._id,
        userId: this.user['_id'],
        groupId: this.user['group']?._id,
        surveyType: this.surveyType as 'values_based' | 'faith_based',
        purchasedByUserId: this.user['_id'] !== this.currentUserId ? this.currentUserId : undefined,
      },
      class: 'modal-md',
    });
    
    modalRef.content?.onPurchase.subscribe((report: ArchetypeReport) => {
      this.archetypeReports.set(surveyResult._id, report);
    });
  }
  
  /**
   * Get display name for an archetype
   */
  getArchetypeDisplayName(archetype: string): string {
    return this.archetypeDisplayNames[archetype as keyof typeof this.archetypeDisplayNames] || archetype;
  }

  ngAfterViewChecked(): void {
    if (this.canvas && !this.canvasSubscription) {
      this.canvasSubscription = this.canvas.changes.subscribe((change) => {
        this.ipsatResults.forEach((result) => {
          const ctx = document.getElementById(
            `doughnut${result._id}`
          ) as HTMLCanvasElement | null;

          if (ctx) {
            const chartInstance = new Chart(ctx, {
              type: 'doughnut',
              data: {
                labels: this.surveyResultCalculationsService.doughnutLabels(
                  this.surveyType
                ),
                datasets: [
                  {
                    label: 'Your IPSAT Score breakdown',
                    data: result.chartData,
                    backgroundColor: CHART_COLORS,
                  },
                ],
              },
              options: {
                elements: {
                  arc: {
                    borderWidth: 0,
                  },
                },
                plugins: {
                  legend: {
                    display: false,
                  },
                },
              },
            });

            // Store the chart instance
            this.chartInstances.push(chartInstance as Chart);
          }
        });
      });
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from canvas changes subscription
    if (this.canvasSubscription) {
      this.canvasSubscription.unsubscribe();
    }

    // Destroy each chart instance
    this.chartInstances.forEach(chart => chart.destroy());
  }
}

// angular.module('nexleader-ipsat').directive(
//   'ng17NexleaderSurveyResultsComponent',
//   downgradeComponent({
//     component: NexleaderSurveyResultsComponent,
//   }) as angular.IDirectiveFactory
// );
